import React, {useCallback} from 'react';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {DraftInput} from 'components/customer/Product/Drawer/DraftInput';

interface FaceHeightInputProps {
    runnerIndex: number;
    value: number;
    pixels: number;
    disabled?: boolean;
    onSet: (index: number, height: number) => void;
}

// Writes the same field as the panel on every keystroke, so the two never
// disagree; emptying it writes 0, as the panel does.
export const FaceHeightInput = ({
    runnerIndex,
    value,
    pixels,
    disabled = false,
    onSet,
}: FaceHeightInputProps) => {
    const {clearDraft, accept, isHeld, save} = useFaceDraft();

    const commitHandler = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            const text = event.currentTarget.value;
            const height = parseFloat(text.trim());
            const next = height > 0 ? height : 0;

            clearDraft();

            // tabbing through is not an edit, and would take a face off auto
            if (accept(runnerIndex, next, text, value) && next !== value) {
                onSet(runnerIndex, next);
            }
        },
        [accept, clearDraft, onSet, runnerIndex, value]
    );

    // Enter applies the height, then saves the product as other fields do,
    // unless the height does not fit
    const enterHandler = useCallback(() => {
        if (!isHeld(runnerIndex)) save();
    }, [isHeld, runnerIndex, save]);

    return (
        <DraftInput
            index={runnerIndex}
            value={value}
            pixels={pixels}
            dataCy={`drawer-height-${runnerIndex}`}
            ariaLabel={`Drawer ${runnerIndex + 1} face height in mm`}
            disabled={disabled}
            title={
                disabled
                    ? 'One drawer has to stay on auto to take up the leftover space'
                    : undefined
            }
            onBlur={commitHandler}
            onEnter={enterHandler}
        />
    );
};
